import { useCallback } from 'react';

import { type CloseAssetModalLocations, useTrackAssetModalClosed } from '@air/analytics';

/**
 * Public-area owner of asset-modal close. Deliberately duplicated from the private
 * twin (`useClosePrivateAssetModal`) per rules/area-isolation.md — URL schemes are
 * per-area architecture. The contracts are free to diverge later.
 *
 * Surface-blind: the raw close arrives as an argument — whatever the modal's mounting surface
 * supplied (stripping `/c/{clipId}` on the URL-backed surfaces, clearing editor state in the
 * collection link editor's Edit tab); this hook only adds the analytics.
 */
export type UseClosePublicAssetModalProps = {
  trackLocation: CloseAssetModalLocations;
  /**
   * Close this asset modal — the mounting surface's own mechanism, read off the surface's
   * asset-modal navigation contract at the modal root and threaded here on props.
   */
  onCloseAssetModal: () => void;
};

export const useClosePublicAssetModal = ({ trackLocation, onCloseAssetModal }: UseClosePublicAssetModalProps) => {
  const { trackAssetModalClosed } = useTrackAssetModalClosed();

  const closeAssetModal = useCallback(() => {
    onCloseAssetModal();
    trackAssetModalClosed({ location: trackLocation });
  }, [onCloseAssetModal, trackAssetModalClosed, trackLocation]);

  return {
    closeAssetModal,
  };
};
